// Join page (#/join/<code>): members join directly, strangers enter a
// display name and join as guests (plan 0002, D14).

import { html, LitElement } from "lit";
import { ApiError, hosted } from "../lib/api";
import { store } from "../lib/store";
import type { JoinInfo, JoinResult } from "../lib/types";

export class MmJoin extends LitElement {
  static override properties = {
    code: { attribute: false },
  };

  code = "";

  private info: JoinInfo | null = null;
  private joined: JoinResult | null = null;
  private error = "";
  private busy = false;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      this.info = await hosted.joinInfo(this.code);
      // Signed-in users join immediately — no name prompt.
      if (store.get().user) await this.join();
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "invite link not recognized";
    }
    this.requestUpdate();
  }

  private async join(guestName?: string): Promise<void> {
    this.busy = true;
    this.requestUpdate();
    try {
      this.joined = await hosted.join(this.code, guestName);
      this.error = "";
    } catch (e) {
      this.error = e instanceof ApiError ? e.message : "could not join";
    }
    this.busy = false;
    this.requestUpdate();
  }

  private submitGuest(e: Event): void {
    e.preventDefault();
    const input = this.querySelector("input[name=guest_name]") as HTMLInputElement;
    void this.join(input.value.trim());
  }

  override render() {
    if (this.joined) {
      const me = this.joined.participant;
      return html`<mm-call
        .meetingId=${this.joined.meeting_id}
        .selfParticipantId=${me.id}
        .guestToken=${this.joined.guest_token}
        .meetingTitle=${this.joined.title}
        .isHost=${me.role === "host"}
      ></mm-call>`;
    }
    return html`
      <section class="summary">
        <p class="eyebrow">Join meeting</p>
        ${this.info
          ? html`<h1>${this.info.title}</h1>
              <p class="muted">hosted by ${this.info.host}</p>`
          : html`<h1>Join</h1>`}
      </section>
      ${this.error
        ? html`<div class="message-card message-card-error"><p>${this.error}</p></div>`
        : ""}
      ${this.info && !store.get().user
        ? html`<section>
            <form class="login-form" @submit=${this.submitGuest}>
              <div>
                <label class="field-label" for="guest_name">Your name</label>
                <input
                  id="guest_name"
                  name="guest_name"
                  type="text"
                  required
                  maxlength="60"
                  placeholder="shown next to what you say"
                />
              </div>
              <button class="primary-button" ?disabled=${this.busy}>
                ${this.busy ? "Joining…" : "Join meeting"}
              </button>
            </form>
            <p class="muted" style="margin-top: 0.9rem">
              Have an account? <a class="navlink" href="#/login">Sign in</a> first
              and re-open the invite link.
            </p>
          </section>`
        : ""}
    `;
  }
}

customElements.define("mm-join", MmJoin);
